// EvaluacionEscritaPage.js
// Modo Evaluación ESCRITA: sin micrófono ni Realtime. Trae las preguntas por HTTP
// (banco de preguntas sugeridas y/o generadas), las muestra una a una y califica
// cada respuesta server-side. Reutiliza el guardado de la evaluación realtime.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { useProject } from '../contexts/ProjectContext';
import { useIdioma } from '../contexts/IdiomaContext';
import { evaluacionEscritaService } from '../services/api';
import MuroCursos from '../components/landing/sections/MuroCursos';

const slideUp = keyframes`from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}`;
const countup = keyframes`from{transform:scale(.6);opacity:0}60%{transform:scale(1.08);opacity:1}to{transform:scale(1)}`;

const Page = styled.div`
  min-height:100vh;
  background:
    radial-gradient(circle at 20% 10%, rgba(8,145,178,.25) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(34,211,238,.18) 0%, transparent 50%),
    linear-gradient(160deg,#07111d 0%,#0a1f3a 100%);
  color:#e2e8f0; display:flex; flex-direction:column;
  padding:clamp(.75rem,2vw,1.5rem);
  @media(min-width:769px){padding-top:88px}
  font-family:'Inter',-apple-system,system-ui,sans-serif;
`;
const Container = styled.div`width:100%;max-width:760px;margin:0 auto;flex:1;display:flex;flex-direction:column;gap:.9rem;`;
const TopBar = styled.div`display:flex;align-items:center;justify-content:space-between;padding:.25rem .1rem;`;
const DocTitle = styled.div`font-size:.82rem;opacity:.7;font-weight:600;display:flex;align-items:center;gap:.5rem;`;
const IdiomaPill = styled.span`background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.12);padding:2px 9px;border-radius:999px;font-size:.7rem;font-weight:700;`;
const Dots = styled.div`display:flex;gap:6px;padding:.4rem 0;`;
const Dot = styled.div`flex:1;height:6px;border-radius:999px;transition:background .4s;
  background:${p => p.$done ? 'linear-gradient(90deg,#06b6d4,#22d3ee)' : p.$current ? 'rgba(34,211,238,.4)' : 'rgba(255,255,255,.08)'};`;
const Card = styled.div`
  background:linear-gradient(160deg,rgba(255,255,255,.075) 0%,rgba(255,255,255,.035) 100%);
  border:1px solid rgba(255,255,255,.1);border-radius:22px;
  padding:clamp(1.5rem,3.5vw,2.25rem) clamp(1.25rem,3.5vw,2rem);
  backdrop-filter:blur(20px);box-shadow:0 12px 40px rgba(0,0,0,.35);
  animation:${slideUp} .5s cubic-bezier(.16,1,.3,1);
`;
const TipoChip = styled.span`
  display:inline-flex;align-items:center;gap:6px;background:linear-gradient(135deg,rgba(34,211,238,.18),rgba(6,182,212,.1));
  color:#67e8f9;border:1px solid rgba(34,211,238,.35);padding:5px 12px;border-radius:999px;
  font-size:.7rem;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:1.25rem;`;
const PreguntaNum = styled.div`font-size:.72rem;opacity:.55;font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-bottom:.6rem;`;
const Pregunta = styled.div`font-size:clamp(1.15rem,3vw,1.5rem);line-height:1.55;color:#f8fafc;`;
const Textarea = styled.textarea`
  width:100%;box-sizing:border-box;background:rgba(0,0,0,.25);color:#fff;border:1px solid rgba(255,255,255,.1);
  border-radius:12px;padding:.85rem 1rem;font:inherit;font-size:.95rem;line-height:1.4;resize:none;min-height:90px;margin-top:1rem;
  &:focus{outline:none;border-color:#22d3ee;box-shadow:0 0 0 3px rgba(34,211,238,.15)}
  &:disabled{opacity:.5}
`;
const OpcionBtn = styled.button`
  display:block;width:100%;text-align:left;margin-top:.6rem;padding:.85rem 1rem;border-radius:12px;cursor:pointer;
  background:${p => p.$sel ? 'linear-gradient(135deg,#0891b2,#0e7490)' : 'rgba(255,255,255,.05)'};
  color:#fff;border:1px solid ${p => p.$sel ? 'transparent' : 'rgba(255,255,255,.15)'};font-size:.95rem;font-weight:600;
  transition:transform .1s;&:hover{transform:translateY(-1px)}&:disabled{opacity:.5;cursor:not-allowed}
`;
const Btn = styled.button`
  background:${p => p.$primary ? 'linear-gradient(135deg,#0891b2,#0e7490)' : 'rgba(255,255,255,.08)'};
  color:#fff;border:1px solid ${p => p.$primary ? 'transparent' : 'rgba(255,255,255,.15)'};
  border-radius:12px;padding:.75rem 1.25rem;font-weight:700;font-size:.9rem;cursor:pointer;transition:transform .15s;margin-top:1rem;
  &:hover{transform:translateY(-1px)}&:disabled{opacity:.45;cursor:not-allowed;transform:none}
`;
const Hint = styled.div`font-size:.78rem;opacity:.5;text-align:center;margin-top:.5rem;`;
const ResultCard = styled(Card)`text-align:center;`;
const Score = styled.div`
  font-size:clamp(4rem,14vw,6.5rem);font-weight:900;line-height:1;margin:.5rem 0;
  background:${p => p.$ok ? 'linear-gradient(135deg,#34d399,#10b981)' : 'linear-gradient(135deg,#fca5a5,#ef4444)'};
  -webkit-background-clip:text;background-clip:text;color:transparent;animation:${countup} .7s cubic-bezier(.34,1.56,.64,1);`;
const Badge = styled.div`
  display:inline-flex;padding:.5rem 1.1rem;border-radius:999px;font-size:.85rem;font-weight:800;
  background:${p => p.$ok ? 'rgba(16,185,129,.18)' : 'rgba(239,68,68,.18)'};color:${p => p.$ok ? '#34d399' : '#fca5a5'};
  border:1px solid ${p => p.$ok ? 'rgba(16,185,129,.45)' : 'rgba(239,68,68,.45)'};`;
const FeedbackBox = styled.div`
  margin-top:1rem;border-radius:12px;padding:.8rem 1rem;font-size:.9rem;
  border-left:4px solid ${p => p.$color};background:rgba(0,0,0,.25);`;

// ═════ Diseño «claro» (INS y quien lo elija en la ficha → evaluacion_tema.diseno = 'claro') ═════
// Fondo claro, tarjetas blancas y el color del proyecto en botones, avance y resultado.
// Colores editables: evaluacion_tema { acento, fondo, texto, error }.
export const temaEvaluacion = (proyecto) => {
  const cj = proyecto?.config_json || {};
  const t = cj.evaluacion_tema || {};
  if (t.diseno !== 'claro') return null;
  return {
    $acento: t.acento || proyecto?.color_primario || '#32b5a9',
    $fondo: t.fondo || '#ffffff',
    $tarjeta: t.tarjeta || '#e8f7f6',
    $texto: t.texto || '#3f3f3e',
    $error: t.error || '#e1312a',
    $fuente: cj.landing_config?.fuente_principal || "'Nunito', Helvetica, Arial, sans-serif",
  };
};
export const C = {
  Page: styled.div`
    --acento: ${p => p.$acento || '#32b5a9'}; --fondo: ${p => p.$fondo || '#ffffff'}; --tarjeta: ${p => p.$tarjeta || '#e8f7f6'};
    --texto: ${p => p.$texto || '#3f3f3e'}; --error: ${p => p.$error || '#e1312a'};
    min-height: 100vh; background: var(--fondo); color: var(--texto); display: flex; flex-direction: column;
    padding: clamp(1rem, 3vw, 2.5rem) clamp(.75rem, 2vw, 1.5rem);
    font-family: ${p => p.$fuente || "'Nunito', Helvetica, Arial, sans-serif"};
  `,
  Container: styled.div`width: 100%; max-width: 760px; margin: 0 auto; flex: 1; display: flex; flex-direction: column; gap: .9rem;`,
  TopBar: styled.div`display: flex; align-items: center; justify-content: space-between; padding: .25rem .1rem; color: var(--texto);`,
  DocTitle: styled.div`font-size: .9rem; font-weight: 800; color: var(--acento); display: flex; align-items: center; gap: .5rem;`,
  IdiomaPill: styled.span`background: #fff; color: var(--acento); border: 1px solid var(--acento); padding: 1px 9px; border-radius: 999px; font-size: .7rem; font-weight: 800;`,
  Dots: styled.div`display: flex; gap: 6px; padding: .4rem 0;`,
  Dot: styled.div`flex: 1; height: 7px; border-radius: 999px; transition: background .4s, opacity .4s;
    background: ${p => (p.$done || p.$current ? 'var(--acento)' : '#cfe6e3')}; opacity: ${p => (p.$current ? .45 : 1)};`,
  Card: styled.div`
    background: var(--tarjeta); border: 1px solid #d3ebe8; border-radius: 16px;
    padding: clamp(1.5rem, 3.5vw, 2.25rem) clamp(1.25rem, 3.5vw, 2rem);
    box-shadow: 0 10px 30px rgba(20, 90, 82, .08);
    animation: ${slideUp} .5s cubic-bezier(.16,1,.3,1);
  `,
  TipoChip: styled.span`display: inline-flex; align-items: center; gap: 6px; background: #fff; color: var(--acento);
    border: 1px solid var(--acento); padding: 4px 12px; border-radius: 999px; font-size: .72rem; font-weight: 800;
    letter-spacing: 1px; text-transform: uppercase; margin-bottom: 1.1rem;`,
  PreguntaNum: styled.div`font-size: .78rem; color: #7a8a8c; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: .55rem;`,
  Pregunta: styled.div`font-size: clamp(1.15rem, 2.8vw, 1.45rem); line-height: 1.45; font-weight: 700; color: #1f2d2c;`,
  Textarea: styled.textarea`
    width: 100%; box-sizing: border-box; background: #fff; color: var(--texto); border: 1px solid #cfe3e1;
    border-radius: 10px; padding: .85rem 1rem; font: inherit; font-size: 1rem; line-height: 1.45; resize: none; min-height: 90px; margin-top: 1rem;
    &:focus { outline: none; border-color: var(--acento); box-shadow: 0 0 0 3px rgba(50, 181, 169, .18); }
    &:disabled { opacity: .55; }
  `,
  OpcionBtn: styled.button`
    display: block; width: 100%; text-align: left; margin-top: .6rem; padding: .85rem 1rem; border-radius: 10px; cursor: pointer;
    font: inherit; font-size: 1rem; font-weight: 600; line-height: 1.4;
    background: ${p => (p.$sel ? 'var(--acento)' : '#fff')}; color: ${p => (p.$sel ? '#fff' : '#2c3a39')};
    border: 1px solid ${p => (p.$sel ? 'var(--acento)' : '#cfe3e1')};
    transition: border-color .15s, transform .1s, background .15s;
    &:hover:not(:disabled) { border-color: var(--acento); transform: translateY(-1px); }
    &:disabled { opacity: .55; cursor: not-allowed; }
  `,
  Btn: styled.button`
    background: ${p => (p.$primary ? 'var(--acento)' : '#fff')}; color: ${p => (p.$primary ? '#fff' : 'var(--acento)')};
    border: 1px solid var(--acento); border-radius: 10px; padding: .75rem 1.3rem; font: inherit; font-weight: 800; font-size: .95rem;
    text-transform: uppercase; letter-spacing: .3px; cursor: pointer; margin-top: 1rem; transition: transform .15s, filter .15s;
    &:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); }
    &:disabled { opacity: .45; cursor: not-allowed; transform: none; }
  `,
  Hint: styled.div`font-size: .8rem; color: #7a8a8c; text-align: center; margin-top: .5rem;`,
  FeedbackBox: styled.div`margin-top: 1rem; border-radius: 10px; padding: .85rem 1rem; font-size: .95rem; line-height: 1.5;
    border-left: 4px solid ${p => p.$color}; background: #fff; color: var(--texto);`,
};
// Resultado en el diseño claro: anillo con la nota en el color del proyecto (o rojo si no aprobó).
const ResultadoC = styled.div`
  text-align: center;
  .icono { width: 64px; height: 64px; margin: 0 auto .4rem; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-size: 2rem; font-weight: 900; color: #fff; background: ${p => (p.$ok ? 'var(--acento)' : 'var(--error)')}; }
  .nota { font-size: clamp(3.6rem, 12vw, 5.5rem); font-weight: 900; line-height: 1; margin: .4rem 0; color: ${p => (p.$ok ? 'var(--acento)' : 'var(--error)')};
    animation: ${countup} .7s cubic-bezier(.34,1.56,.64,1); }
  .nota small { font-size: .4em; }
  .estado { display: inline-flex; padding: .45rem 1.1rem; border-radius: 999px; font-size: .85rem; font-weight: 800; letter-spacing: .5px;
    color: ${p => (p.$ok ? 'var(--acento)' : 'var(--error)')}; background: ${p => (p.$ok ? '#fff' : '#fdecea')};
    border: 1px solid ${p => (p.$ok ? 'var(--acento)' : 'var(--error)')}; }
  .meta { margin: 1.1rem 0 0; color: #6b7a7b; font-size: .95rem; }
`;
const DetalleC = styled.details`
  margin-top: 1rem; background: var(--tarjeta); border: 1px solid #d3ebe8; border-radius: 14px; padding: .8rem 1rem;
  summary { cursor: pointer; font-weight: 800; color: var(--acento); }
  .item { margin-top: .7rem; padding: .8rem .9rem; background: #fff; border-radius: 10px; border-left: 3px solid var(--c); }
  .cab { font-size: .8rem; font-weight: 800; color: var(--c); text-transform: uppercase; letter-spacing: .4px; }
  .q { margin-top: 4px; font-weight: 700; color: #1f2d2c; }
  .r { margin-top: 6px; font-size: .9rem; color: var(--texto); }
  .r em { color: #7a8a8c; font-style: normal; font-weight: 700; }
  .fb { margin-top: 4px; font-size: .9rem; color: #5b6a6b; }
  .por { margin-top: 6px; padding: .55rem .65rem; border-radius: 8px; background: var(--tarjeta); font-size: .88rem; line-height: 1.55; }
`;
const NIVEL_TXT_C = { completa: 'Correcta', parcial: 'Parcialmente correcta', minima: 'Incompleta', incorrecta: 'Incorrecta' };

const TIPO_LABEL = { vf: '✓/✗ Verdadero o Falso', seleccion: '◉ Selección múltiple', multiple: '☑ Varias respuestas correctas', abierta: '✎ Respuesta abierta', completar: '_ Completar', lista: '☰ Enumeración' };
const NIVEL_COLOR = { completa: '#10b981', parcial: '#06b6d4', minima: '#f59e0b', incorrecta: '#ef4444' };
const NIVEL_EMOJI = { completa: '✅', parcial: '👍', minima: '🤔', incorrecta: '❌' };

// Piezas del diseño de siempre (todos los proyectos menos los de portada tipo streaming).
const D = { Page, Container, TopBar, DocTitle, IdiomaPill, Dots, Dot, Card, TipoChip, PreguntaNum, Pregunta, Textarea, OpcionBtn, Btn, Hint, FeedbackBox };

// ═════ Diseño «streaming» (Learning) ═════
// Proyectos cuya portada es catalogo_streaming: fondo y acento del catálogo, Bricolage
// condensada en los títulos y los mismos botones de la portada (blanco = acción, gris = volver).
const FUENTE_S = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const NIVEL_S = {
  completa: { txt: 'Correcta', color: '#3ddc84' },
  parcial: { txt: 'Parcialmente correcta', color: '#5ac8fa' },
  minima: { txt: 'Incompleta', color: '#f5a524' },
  incorrecta: { txt: 'Incorrecta', color: 'var(--acento)' },
};
const TIPO_S = { vf: 'Verdadero o falso', seleccion: 'Selección múltiple', multiple: 'Varias respuestas correctas', abierta: 'Respuesta abierta', completar: 'Completar', lista: 'Enumeración' };
const S = {
  Page: styled.div`
    --acento: ${p => p.$acento || '#E50914'}; --fondo: ${p => p.$fondo || '#141414'};
    min-height: 100vh; background: var(--fondo); color: #d5d9df; font-family: ${FUENTE_S};
    display: flex; flex-direction: column; padding: clamp(5.5rem, 9vw, 7.5rem) 16px clamp(2.5rem, 5vw, 4rem);
    @media (max-width: 760px) { padding-top: 5rem; }
  `,
  Container: styled.div`width: 100%; max-width: 820px; margin: 0 auto; flex: 1; display: flex; flex-direction: column; gap: 1rem;`,
  TopBar: styled.div`display: flex; align-items: baseline; justify-content: space-between; gap: 1rem;`,
  DocTitle: styled.div`display: flex; align-items: center; gap: .6rem; min-width: 0; font-size: .95rem; font-weight: 600; color: #e7eaee;
    > span:first-child { display: none; }
    &::before { content: ''; flex: none; width: 28px; height: 2px; background: var(--acento); }`,
  IdiomaPill: styled.span`flex: none; padding: .1rem .5rem; border-radius: 4px; background: rgba(255,255,255,.1); font-size: .72rem; font-weight: 700; color: #cfd3d8;`,
  Dots: styled.div`display: flex; gap: 4px;`,
  Dot: styled.div`flex: 1; height: 4px; border-radius: 2px; transition: background .3s;
    background: ${p => p.$done ? 'var(--acento)' : p.$current ? 'rgba(255,255,255,.55)' : 'rgba(255,255,255,.14)'};`,
  Card: styled.div`
    background: #1d1d1d; border-radius: 8px; box-shadow: inset 0 0 0 1px rgba(255,255,255,.06);
    padding: clamp(1.4rem, 3.5vw, 2.2rem) clamp(1.2rem, 3.5vw, 2.2rem);
    animation: ${slideUp} .4s cubic-bezier(.16,1,.3,1);
    @media (prefers-reduced-motion: reduce) { animation: none; }
  `,
  TipoChip: styled.span`display: inline-block; margin-bottom: .9rem; font-size: .85rem; font-weight: 600; color: #9aa1ab;`,
  PreguntaNum: styled.div`margin-bottom: .45rem; font-size: .85rem; font-weight: 600; color: #9aa1ab; font-variant-numeric: tabular-nums;`,
  Pregunta: styled.div`font-size: clamp(1.25rem, 2.6vw, 1.6rem); font-weight: 700; font-stretch: 92%; line-height: 1.3; color: #fff; text-wrap: pretty;`,
  Textarea: styled.textarea`
    width: 100%; box-sizing: border-box; margin-top: 1.1rem; min-height: 110px; resize: vertical; padding: .9rem 1rem; border-radius: 6px;
    background: #141414; color: #fff; border: 1px solid rgba(255,255,255,.16); font: inherit; font-size: 1rem; line-height: 1.5;
    &::placeholder { color: #6f7680; }
    &:focus { outline: none; border-color: #fff; }
    &:disabled { opacity: .5; }
  `,
  OpcionBtn: styled.button`
    display: block; width: 100%; margin-top: .55rem; padding: .95rem 1.1rem; border-radius: 6px; text-align: left; cursor: pointer;
    font: inherit; font-size: 1rem; font-weight: 600; color: #fff; transition: background .15s, box-shadow .15s;
    background: ${p => p.$sel ? 'var(--acento)' : '#262626'}; border: 0; box-shadow: inset 0 0 0 1px ${p => p.$sel ? 'transparent' : 'rgba(255,255,255,.08)'};
    &:hover:not(:disabled) { background: ${p => p.$sel ? 'var(--acento)' : '#303030'}; }
    &:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
    &:disabled { opacity: .55; cursor: not-allowed; }
  `,
  Btn: styled.button`
    display: inline-flex; align-items: center; justify-content: center; gap: .55rem; height: 50px; padding: 0 1.5rem; margin-top: 1.2rem;
    border: 0; border-radius: 8px; cursor: pointer; font: inherit; font-size: 1rem; font-weight: 700; transition: background .2s, transform .15s;
    ${p => p.$primary
      ? css`background: #fff; color: #111; &:hover:not(:disabled) { background: rgba(255,255,255,.86); }`
      : css`background: rgba(109,109,110,.6); color: #fff; &:hover:not(:disabled) { background: rgba(109,109,110,.4); }`}
    svg { width: 20px; height: 20px; }
    &:active:not(:disabled) { transform: scale(.98); }
    &:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
    &:disabled { opacity: .45; cursor: not-allowed; }
  `,
  Hint: styled.div`margin-top: .6rem; font-size: .82rem; color: #7d848e;`,
  FeedbackBox: styled.div`
    margin-top: 1.1rem; padding: .9rem 1.1rem; border-radius: 6px; background: #141414; font-size: .95rem; line-height: 1.5; color: #d5d9df;
    box-shadow: inset 3px 0 0 ${p => p.$color};
  `,
};

// Entrada y resultado van sobre el banner de portadas de los cursos (MuroCursos), como
// «Mis certificados», «Nosotros» y los documentos legales.
const RaizS = styled.div`
  --acento: ${p => p.$acento || '#E50914'}; --fondo: ${p => p.$fondo || '#141414'};
  min-height: 100vh; background: var(--fondo); color: #d5d9df; font-family: ${FUENTE_S};
`;
const CabeceraS = styled.header`
  position: relative; overflow: hidden; isolation: isolate; min-height: clamp(460px, 66vh, 640px);
  display: flex; flex-direction: column; justify-content: flex-end; padding: clamp(8rem, 12vw, 9rem) 4vw clamp(2rem, 4vw, 3rem);
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .95rem; font-weight: 600; color: #e7eaee; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  .bajada { margin: 1.1rem 0 0; max-width: 40rem; font-size: 1.08rem; line-height: 1.55; color: #d0d5dc; }
  .bajada b { color: #fff; font-weight: 600; }
  .botones { display: flex; flex-wrap: wrap; gap: .7rem; margin-top: .4rem; }
`;
// Entrada: título del examen y sus condiciones.
const EntradaS = styled(CabeceraS)`
  h1 { margin: .9rem 0 0; max-width: 20ch; font-size: clamp(2.6rem, 7vw, 5.6rem); font-weight: 800; font-stretch: 76%; line-height: .92; letter-spacing: -.02em; color: #fff; text-wrap: balance; }
  dl { display: flex; flex-wrap: wrap; gap: .7rem; margin: 1.6rem 0 0; }
  dl div { display: flex; align-items: baseline; gap: .5rem; padding: .6rem 1.1rem; border-radius: 999px;
    background: rgba(0,0,0,.42); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
  dt { order: 2; font-size: .9rem; color: #c3c9d1; }
  dd { margin: 0; font-size: 1.3rem; font-weight: 800; color: #fff; font-variant-numeric: tabular-nums; }
  .aviso { margin: 1.2rem 0 0; color: #f5a524; font-weight: 600; }
`;

// Resultado: la nota en un anillo grande (como el avance del tablero) y el veredicto al lado.
const llenarAnillo = keyframes`from { stroke-dashoffset: var(--total); }`;
const ResultadoS = styled(CabeceraS)`
  .resumen { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 1.5rem clamp(1.5rem, 4vw, 3rem); }
  figure { position: relative; margin: 0; width: clamp(160px, 22vw, 220px); aspect-ratio: 1; border-radius: 50%;
    background: rgba(0,0,0,.45); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
  figure svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  circle { fill: none; stroke-width: 8; }
  .base { stroke: rgba(255,255,255,.12); }
  .valor { stroke: ${p => p.$color}; stroke-linecap: round; stroke-dasharray: var(--total); stroke-dashoffset: var(--resta);
    animation: ${llenarAnillo} 1.3s .2s cubic-bezier(.3,.7,.2,1) both; }
  figcaption { position: absolute; inset: 0; display: grid; place-content: center; text-align: center; }
  figcaption b { font-size: clamp(3rem, 6vw, 4.4rem); font-weight: 800; font-stretch: 76%; line-height: 1; color: #fff; font-variant-numeric: tabular-nums; }
  figcaption b small { font-size: .42em; color: #9aa1ab; }
  h1 { margin: .6rem 0 0; font-size: clamp(2.4rem, 6vw, 4.4rem); font-weight: 800; font-stretch: 76%; line-height: .95; letter-spacing: -.02em; color: ${p => p.$color}; }
  .cert { margin-top: 1rem; padding: .8rem 1rem; border-radius: 6px; background: rgba(61,220,132,.1); color: #c9f5dc; font-size: .95rem; }
  @media (max-width: 640px) { .resumen { grid-template-columns: 1fr; } figure { width: 150px; } }
  @media (prefers-reduced-motion: reduce) { .valor { animation: none; } }
`;
// Repaso: una fila por pregunta, numeradas en el orden en que salieron.
const RepasoS = styled.section`
  max-width: 980px; padding: clamp(2rem, 4vw, 3rem) 4vw clamp(3rem, 6vw, 5rem);
  h2 { display: flex; align-items: baseline; gap: .6rem; margin: 0 0 .8rem; font-size: clamp(1.3rem, 2.2vw, 1.7rem); font-weight: 800; font-stretch: 85%; color: #fff; }
  h2 small { font-size: .9rem; font-weight: 600; font-stretch: 100%; color: #8a929d; }
  ol { list-style: none; margin: 0; padding: 0; border-top: 1px solid rgba(255,255,255,.1); }
  li { display: grid; grid-template-columns: 2.6rem minmax(0, 1fr); gap: 1rem; padding: 1.1rem 0; border-bottom: 1px solid rgba(255,255,255,.1); }
  .n { font-size: 2rem; font-weight: 800; font-stretch: 76%; line-height: 1; color: #4a4f57; font-variant-numeric: tabular-nums; }
  .q { margin: 0; font-size: 1.05rem; font-weight: 650; line-height: 1.4; color: #fff; }
  .estado { display: inline-block; margin-bottom: .35rem; font-size: .82rem; font-weight: 700; }
  .r { margin: .45rem 0 0; font-size: .92rem; line-height: 1.5; color: #c9ced5; }
  .r span { color: #8a929d; }
  .fb { margin: .45rem 0 0; font-size: .9rem; line-height: 1.5; color: #9aa1ab; }
  /* La explicación de por qué: lo que el alumno viene a leer al final. */
  .retro { margin: .6rem 0 0; padding: .7rem .85rem; border-radius: 10px; font-size: .92rem;
           line-height: 1.6; color: #e6e9ee; background: rgba(255,255,255,.06);
           border-left: 3px solid var(--acento, #E50914); }
  .retro b { display: block; margin-bottom: .2rem; font-size: .74rem; letter-spacing: .08em;
             text-transform: uppercase; color: #9aa1ab; font-weight: 700; }
`;
// Cierre del examen: qué domina y qué reforzar, en una tarjeta aparte.
const CierreS = styled.aside`
  max-width: 980px; margin: 0 4vw; padding: 1.1rem 1.25rem; border-radius: 14px;
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.12);
  b { display: block; margin-bottom: .3rem; font-size: .74rem; letter-spacing: .08em;
      text-transform: uppercase; color: #9aa1ab; font-weight: 700; }
  p { margin: 0; font-size: 1rem; line-height: 1.65; color: #e6e9ee; }
`;
// Lo usa también la evaluación oral y la pantalla de elección (EvaluacionRealtimePage).
export const catalogoStreaming = (proyecto) => (proyecto?.config_json?.landing_secciones || []).find(x => x.tipo === 'catalogo_streaming' && x.visible !== false)?.config || null;
export const temaStreaming = (proyecto) => {
  const cat = catalogoStreaming(proyecto);
  return cat ? { $acento: cat.color_acento || cat.color_boton_registro, $fondo: cat.color_fondo || proyecto?.config_json?.landing_config?.color_fondo } : null;
};
export { S as PiezasStreaming, RaizS, EntradaS, FUENTE_S };
export const PlayIco = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7.5 5v14l11.5-7Z" /></svg>;

const EvaluacionEscritaPage = () => {
  const { documentId } = useParams();
  // ?examen=<id>: examen de un módulo, lección o del curso (mentor_examenes).
  const [searchParams] = useSearchParams();
  const examenId = parseInt(searchParams.get('examen') || '', 10) || null;
  const navigate = useNavigate();
  const { proyecto } = useProject();
  const { t, idioma } = useIdioma();

  const [stage, setStage] = useState('pre'); // pre | cargando | en_curso | finalizando | resultado | error
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [idx, setIdx] = useState(0);
  const [intentoId, setIntentoId] = useState(null);
  const [texto, setTexto] = useState('');
  const [seleccion, setSeleccion] = useState(null);
  const [marcadas, setMarcadas] = useState([]); // preguntas con varias respuestas correctas
  const [thinking, setThinking] = useState(false);
  const [ultimoFeedback, setUltimoFeedback] = useState(null);
  const [resultado, setResultado] = useState(null);

  const respuestasRef = useRef([]);
  const startRef = useRef(null);

  // Portada tipo streaming (Learning): este examen se viste igual que el resto del sitio.
  const catalogo = (proyecto?.config_json?.landing_secciones || []).find(x => x.tipo === 'catalogo_streaming' && x.visible !== false)?.config || null;
  const streaming = !!catalogo;
  // Diseño claro (INS): fondo claro y colores del proyecto, editables en la ficha.
  const claroProps = streaming ? null : temaEvaluacion(proyecto);
  const claro = !!claroProps;
  const { Page, Container, TopBar, DocTitle, IdiomaPill, Dots, Dot, Card, TipoChip, PreguntaNum, Pregunta, Textarea, OpcionBtn, Btn, Hint, FeedbackBox } = streaming ? S : (claro ? C : D);
  const temaProps = streaming ? { $acento: catalogo.color_acento || catalogo.color_boton_registro, $fondo: catalogo.color_fondo || proyecto?.config_json?.landing_config?.color_fondo } : (claroProps || {});
  // Colores de los textos secundarios que van en línea (sobre blanco en el diseño claro).
  const suave = claro ? '#6b7a7b' : 'rgba(255,255,255,.7)';
  const volverCurso = () => navigate(examenId ? `/mentor/${documentId}` : '/contenidos');

  // Datos de la pantalla de entrada (solo en el diseño streaming; no crea intento).
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!streaming) return;
    evaluacionEscritaService.info(parseInt(documentId, 10), examenId).then(r => { if (r.data?.success) setInfo(r.data); }).catch(() => {});
  }, [streaming, documentId, examenId]);

  const total = meta?.total_preguntas || preguntas.length || 0;
  const porcAprob = meta?.porcentaje_aprobacion || 60;
  const actual = preguntas[idx] || null;

  const iniciar = useCallback(async () => {
    setError(null); setStage('cargando'); respuestasRef.current = [];
    try {
      const res = await evaluacionEscritaService.preguntas(parseInt(documentId, 10), proyecto?.id, examenId);
      const d = res.data;
      if (!d?.success) throw new Error(d?.message || 'No se pudieron cargar las preguntas');
      setMeta({ total_preguntas: d.total_preguntas, porcentaje_aprobacion: d.porcentaje_aprobacion, document_title: d.examen_titulo || d.document_title });
      setPreguntas(d.preguntas || []);
      setIntentoId(d.intento_id);
      setIdx(0); setTexto(''); setSeleccion(null); setMarcadas([]); setUltimoFeedback(null);
      startRef.current = Date.now();
      setStage('en_curso');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Error cargando la evaluación');
      setStage('error');
    }
  }, [documentId, proyecto?.id, examenId]);

  const finalizar = useCallback(async () => {
    setStage('finalizando');
    const detalle = respuestasRef.current;
    const totalPts = detalle.reduce((a, r) => a + (r.puntaje || 0), 0);
    const porcentaje = detalle.length ? Math.round((totalPts / detalle.length) * 100) : 0;
    const aprobado = porcentaje >= porcAprob;
    const correctas = detalle.filter(r => r.nivel === 'completa' || r.nivel === 'parcial').length;
    const duracionMin = Math.max(1, Math.round((Date.now() - (startRef.current || Date.now())) / 60000));
    try {
      const res = await evaluacionEscritaService.guardar({
        document_id: parseInt(documentId, 10),
        proyecto_id: proyecto?.id || null,
        total_preguntas: detalle.length,
        preguntas_correctas: correctas,
        porcentaje,
        aprobado,
        detalle_respuestas: detalle,
        duracion_minutos: duracionMin,
        idioma,
        resumen_global: '',
        examen_id: examenId,
      });
      // Con examen, «aprobado» lo decide el servidor.
      setResultado({ ...res.data, porcentaje, aprobado: examenId ? !!res.data?.aprobado : aprobado, porcentaje_aprobacion: res.data?.porcentaje_aprobacion || porcAprob });
      setStage('resultado');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Error guardando el resultado');
      setStage('error');
    }
  }, [documentId, idioma, porcAprob, proyecto?.id, examenId]);

  const enviarRespuesta = useCallback(async (valor) => {
    const respuesta = String(valor ?? '').trim();
    if (!respuesta || !actual || thinking) return;
    setThinking(true);
    try {
      const res = await evaluacionEscritaService.calificar(intentoId, actual.numero, respuesta);
      const d = res.data || {};
      respuestasRef.current.push({
        numero_pregunta: actual.numero,
        tipo_pregunta: actual.tipo,
        pregunta_texto: actual.pregunta_texto,
        respuesta_usuario: respuesta,
        respuesta_correcta: d.respuesta_correcta || '',
        puntaje: d.puntaje ?? 0,
        nivel: d.nivel || 'incorrecta',
        feedback: d.feedback || '',
        explicacion: d.explicacion || '',
      });
      setUltimoFeedback({ nivel: d.nivel, feedback: d.feedback, explicacion: d.explicacion || '', color: NIVEL_COLOR[d.nivel] || '#94a3b8' });
    } catch (e) {
      // registrar como incorrecta para no bloquear el examen
      const motivo = e?.response?.data?.message || 'No se pudo calificar esta respuesta.';
      respuestasRef.current.push({
        numero_pregunta: actual.numero, tipo_pregunta: actual.tipo, pregunta_texto: actual.pregunta_texto,
        respuesta_usuario: respuesta, respuesta_correcta: '', puntaje: 0, nivel: 'incorrecta',
        feedback: motivo,
      });
      setUltimoFeedback({ nivel: 'incorrecta', feedback: motivo, color: NIVEL_COLOR.incorrecta });
    } finally {
      setThinking(false);
    }
  }, [actual, intentoId, thinking]);

  const avanzar = useCallback(() => {
    setUltimoFeedback(null); setTexto(''); setSeleccion(null); setMarcadas([]);
    if (idx + 1 >= preguntas.length) { finalizar(); }
    else setIdx(i => i + 1);
  }, [idx, preguntas.length, finalizar]);

  // ═════ PRE ═════
  if (stage === 'pre' && streaming) {
    const quedan = info?.max_intentos > 0 ? Math.max(0, info.max_intentos - (info.intentos_usados || 0)) : null;
    return (
      <RaizS {...temaProps}>
        <EntradaS>
          <MuroCursos proyecto={proyecto} fondo={temaProps.$fondo || '#141414'} acento={temaProps.$acento || '#E50914'} opacidad={0.6} />
          <span className="eyebrow">Evaluación escrita</span>
          <h1>{info?.titulo || (info?.curso ? `Evaluación de ${info.curso}` : 'Evaluación')}</h1>
          <p className="bajada">
            {info?.titulo && info?.curso && <>Del curso <b>{info.curso}</b>. </>}
            Verás una pregunta a la vez y respondes escribiendo o eligiendo una opción. Al responder sabrás si acertaste.
          </p>
          {info && (
            <dl>
              <div><dd>{info.total_preguntas}</dd><dt>preguntas</dt></div>
              <div><dd>{Math.round(info.porcentaje_aprobacion)} %</dd><dt>para aprobar</dt></div>
              <div>
                <dd>{quedan === null ? 'Libres' : quedan}</dd>
                <dt>{quedan === null ? 'intentos, sin límite' : `${quedan === 1 ? 'intento disponible' : 'intentos disponibles'} de ${info.max_intentos}`}</dt>
              </div>
            </dl>
          )}
          {quedan === 0 && <p className="aviso">Ya usaste todos tus intentos de esta evaluación.</p>}
          <div className="botones">
            <Btn $primary onClick={iniciar} disabled={quedan === 0}><PlayIco /> Comenzar evaluación</Btn>
            <Btn onClick={volverCurso}>Volver al curso</Btn>
          </div>
        </EntradaS>
      </RaizS>
    );
  }
  if (stage === 'pre') {
    return (
      <Page {...temaProps}><Container>
        <Card style={{ textAlign: 'center', paddingTop: '3rem', paddingBottom: '2.5rem' }}>
          {!claro && <div style={{ fontSize: '3.5rem', marginBottom: '.5rem' }}>✍️</div>}
          <h1 style={{ margin: '0 0 .75rem', fontSize: 'clamp(1.5rem,4vw,2rem)', ...(claro ? { color: 'var(--acento)', fontWeight: 800, textTransform: 'uppercase' } : {}) }}>Evaluación escrita</h1>
          <p style={{ color: suave, margin: '0 auto 2rem', maxWidth: 460, lineHeight: 1.6 }}>
            Se te muestra una pregunta a la vez en <strong>{(idioma || 'es').toUpperCase()}</strong> y respondes <strong>escribiendo</strong> (sin micrófono).
          </p>
          <Btn $primary onClick={iniciar} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>Comenzar evaluación</Btn>
        </Card>
      </Container></Page>
    );
  }

  // ═════ CARGANDO ═════
  if (stage === 'cargando' || stage === 'finalizando') {
    return (
      <Page {...temaProps}><Container>
        <Card style={{ textAlign: 'center' }}>
          {!streaming && !claro && <div style={{ fontSize: '2.5rem' }}>⏳</div>}
          <p style={{ opacity: .8 }}>{stage === 'cargando' ? 'Preparando tus preguntas…' : 'Calificando y guardando…'}</p>
        </Card>
      </Container></Page>
    );
  }

  // ═════ ERROR ═════
  if (stage === 'error') {
    return (
      <Page {...temaProps}><Container>
        <Card style={{ textAlign: 'center' }}>
          <h2 style={{ color: claro ? 'var(--error)' : '#fca5a5' }}>{streaming || claro ? 'No se pudo iniciar' : '⚠️ No se pudo iniciar'}</h2>
          <p style={{ color: suave }}>{error}</p>
          <Btn onClick={() => navigate(-1)}>Volver</Btn>
        </Card>
      </Container></Page>
    );
  }

  // ═════ RESULTADO ═════
  if (stage === 'resultado' && resultado && streaming) {
    const ok = resultado.aprobado;
    const pct = Math.round(resultado.porcentaje);
    const minimo = Math.round(resultado.porcentaje_aprobacion || porcAprob);
    const lista = respuestasRef.current;
    const aciertos = lista.filter(r => r.nivel === 'completa').length;
    const color = ok ? '#3ddc84' : 'var(--acento)';
    const R = 52, total = 2 * Math.PI * R;
    const usados = (info?.intentos_usados || 0) + 1;
    const quedan = info?.max_intentos > 0 ? Math.max(0, info.max_intentos - usados) : null;
    return (
      <RaizS {...temaProps}>
        <ResultadoS $color={color}>
          <MuroCursos proyecto={proyecto} fondo={temaProps.$fondo || '#141414'} acento={temaProps.$acento || '#E50914'} opacidad={0.6} />
          <div className="resumen">
          <figure style={{ '--total': total, '--resta': total * (1 - Math.min(100, Math.max(0, pct)) / 100) }}>
            <svg viewBox="0 0 120 120" aria-hidden="true"><circle className="base" cx="60" cy="60" r={R} /><circle className="valor" cx="60" cy="60" r={R} /></svg>
            <figcaption><b>{pct}<small>%</small></b></figcaption>
          </figure>
          <div>
            <span className="eyebrow">{meta?.document_title || 'Evaluación'}</span>
            <h1>{ok ? 'Aprobaste' : 'Aún no apruebas'}</h1>
            <p className="bajada">
              Sacaste <b>{pct} %</b> y necesitabas <b>{minimo} %</b>. Acertaste {aciertos} de {lista.length} {lista.length === 1 ? 'pregunta' : 'preguntas'}.
              {!ok && quedan !== null && <> {quedan === 0 ? 'Ya usaste todos tus intentos.' : quedan === 1 ? 'Te queda 1 intento.' : `Te quedan ${quedan} intentos.`}</>}
            </p>
            {resultado.certificado?.emitido && (
              <p className="cert">Ya tienes tu certificado. Código de verificación: <b>{resultado.certificado.codigo}</b></p>
            )}
            <div className="botones">
              <Btn $primary onClick={volverCurso}>Volver al curso</Btn>
              {resultado.certificado?.emitido && <Btn onClick={() => navigate('/certificados')}>Ver mis certificados</Btn>}
              {!ok && quedan !== 0 && <Btn onClick={() => { setInfo(i => i ? { ...i, intentos_usados: usados } : i); iniciar(); }}>Intentar de nuevo</Btn>}
            </div>
          </div>
          </div>
        </ResultadoS>
        {resultado.resumen_global && (
          <CierreS><b>Qué reforzar</b><p>{resultado.resumen_global}</p></CierreS>
        )}
        <RepasoS>
          <h2>Tus respuestas <small>{aciertos} de {lista.length} correctas</small></h2>
          <ol>
            {lista.map((r, i) => {
              const n = NIVEL_S[r.nivel] || NIVEL_S.incorrecta;
              return (
                <li key={i}>
                  <span className="n" aria-hidden="true">{i + 1}</span>
                  <div>
                    <span className="estado" style={{ color: n.color }}>{n.txt}</span>
                    <p className="q">{r.pregunta_texto}</p>
                    <p className="r"><span>Tu respuesta: </span>{r.respuesta_usuario}</p>
                    {r.respuesta_correcta && r.nivel !== 'completa' && <p className="r"><span>Respuesta esperada: </span>{r.respuesta_correcta}</p>}
                    {r.feedback && <p className="fb">{r.feedback}</p>}
                    {r.explicacion && <p className="retro"><b>Por qué</b>{r.explicacion}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        </RepasoS>
      </RaizS>
    );
  }
  if (stage === 'resultado' && resultado && claro) {
    const ok = resultado.aprobado;
    return (
      <Page {...temaProps}><Container>
        <Card>
          <ResultadoC $ok={ok}>
            <div className="icono" aria-hidden="true">{ok ? '✓' : '!'}</div>
            <div className="nota">{Math.round(resultado.porcentaje)}<small>%</small></div>
            <div className="estado">{ok ? 'APROBADO' : 'NO APROBADO'}</div>
            <p className="meta">
              Necesitabas <strong>{Math.round(resultado.porcentaje_aprobacion || porcAprob)}%</strong> · {respuestasRef.current.length} preguntas
            </p>
          </ResultadoC>
          {resultado.resumen_global && (
            <FeedbackBox $color="var(--acento)">
              <div style={{ fontSize: '.75rem', letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 800, color: 'var(--acento)', marginBottom: 4 }}>Qué reforzar</div>
              <div>{resultado.resumen_global}</div>
            </FeedbackBox>
          )}
          {resultado.certificado?.emitido && (
            <FeedbackBox $color="var(--acento)">Certificado emitido · código <strong>{resultado.certificado.codigo}</strong></FeedbackBox>
          )}
          <div style={{ marginTop: '1rem', display: 'flex', gap: '.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {examenId
              ? <Btn $primary onClick={() => navigate(`/mentor/${documentId}`)}>← Volver al curso</Btn>
              : <>
                  <Btn onClick={() => navigate('/contenidos')}>← {t('ir_contenidos')}</Btn>
                  <Btn $primary onClick={() => navigate('/perfil')}>Mi perfil</Btn>
                </>}
          </div>
        </Card>
        <DetalleC open>
          <summary>Detalle por pregunta ({respuestasRef.current.length})</summary>
          {respuestasRef.current.map((r, i) => {
            const col = r.nivel === 'completa' ? 'var(--acento)' : r.nivel === 'incorrecta' ? 'var(--error)' : (NIVEL_COLOR[r.nivel] || '#94a3b8');
            return (
              <div key={i} className="item" style={{ '--c': col }}>
                <div className="cab">Pregunta {r.numero_pregunta} · {NIVEL_TXT_C[r.nivel] || r.nivel}</div>
                <div className="q">{r.pregunta_texto}</div>
                <div className="r"><em>Tu respuesta:</em> {r.respuesta_usuario}</div>
                {r.respuesta_correcta && <div className="r"><em>Correcta:</em> {r.respuesta_correcta}</div>}
                {/* Sin repetir «La respuesta correcta era…» cuando ya se mostró arriba. */}
                {r.feedback && !(r.respuesta_correcta && /^la respuesta correcta era/i.test(r.feedback.trim())) && <div className="fb">{r.feedback}</div>}
                {r.explicacion && <div className="por"><em>Por qué:</em> {r.explicacion}</div>}
              </div>
            );
          })}
        </DetalleC>
      </Container></Page>
    );
  }
  if (stage === 'resultado' && resultado) {
    const ok = resultado.aprobado;
    return (
      <Page><Container>
        <ResultCard>
          <div style={{ fontSize: '4rem' }}>{ok ? '🎉' : '💪'}</div>
          <Score $ok={ok}>{Math.round(resultado.porcentaje)}<span style={{ fontSize: '.4em' }}>%</span></Score>
          <Badge $ok={ok}>{ok ? '✓ APROBADO' : '× NO APROBADO'}</Badge>
          <p style={{ margin: '1.25rem 0 0', opacity: .7, fontSize: '.92rem' }}>
            Necesitabas <strong>{Math.round(resultado.porcentaje_aprobacion || porcAprob)}%</strong> · {respuestasRef.current.length} preguntas
          </p>
          {resultado.resumen_global && (
            <FeedbackBox $color="#38bdf8" style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '.72rem', letterSpacing: '.08em', textTransform: 'uppercase', opacity: .7, marginBottom: 4 }}>Qué reforzar</div>
              <div style={{ lineHeight: 1.6 }}>{resultado.resumen_global}</div>
            </FeedbackBox>
          )}
          {resultado.certificado?.emitido && (
            <FeedbackBox $color="#10b981" style={{ textAlign: 'left' }}>
              🏆 Certificado emitido · código <strong>{resultado.certificado.codigo}</strong>
            </FeedbackBox>
          )}
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {examenId
              ? <Btn $primary onClick={() => navigate(`/mentor/${documentId}`)}>← Volver al curso</Btn>
              : <>
                  <Btn onClick={() => navigate('/contenidos')}>← {t('ir_contenidos')}</Btn>
                  <Btn $primary onClick={() => navigate('/perfil')}>Mi perfil</Btn>
                </>}
          </div>
        </ResultCard>
        <details open style={{ marginTop: '1rem', background: 'rgba(255,255,255,.04)', borderRadius: 12, padding: '.75rem 1rem' }}>
          <summary style={{ cursor: 'pointer', opacity: .75 }}>Detalle por pregunta ({respuestasRef.current.length})</summary>
          <div style={{ marginTop: '.75rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
            {respuestasRef.current.map((r, i) => (
              <div key={i} style={{ padding: '.75rem', background: 'rgba(0,0,0,.25)', borderRadius: 10, borderLeft: `3px solid ${NIVEL_COLOR[r.nivel] || '#94a3b8'}` }}>
                <div style={{ fontSize: '.8rem', opacity: .7 }}>Pregunta {r.numero_pregunta} · {NIVEL_EMOJI[r.nivel]} {r.nivel}</div>
                <div style={{ marginTop: 4, fontWeight: 500 }}>{r.pregunta_texto}</div>
                <div style={{ marginTop: 6, fontSize: '.85rem', opacity: .85 }}><em>Tu respuesta:</em> {r.respuesta_usuario}</div>
                {r.respuesta_correcta && <div style={{ marginTop: 2, fontSize: '.85rem', opacity: .7 }}><em>Correcta:</em> {r.respuesta_correcta}</div>}
                <div style={{ marginTop: 4, fontSize: '.85rem', color: NIVEL_COLOR[r.nivel] || '#94a3b8' }}>{r.feedback}</div>
                {r.explicacion && (
                  <div style={{ marginTop: 6, padding: '.5rem .6rem', borderRadius: 8, background: 'rgba(255,255,255,.06)', fontSize: '.85rem', lineHeight: 1.55, color: 'rgba(255,255,255,.85)' }}>
                    <em style={{ opacity: .65 }}>Por qué:</em> {r.explicacion}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      </Container></Page>
    );
  }

  // ═════ EN CURSO ═════
  const respondida = !!ultimoFeedback;
  const esMultiple = actual?.tipo === 'multiple';
  const esOpciones = actual && (actual.tipo === 'vf' || actual.tipo === 'seleccion');
  const opciones = actual?.tipo === 'vf' ? (actual.opciones?.length ? actual.opciones : ['Verdadero', 'Falso']) : (actual?.opciones || []);
  const alternarMarcada = (o) => setMarcadas(prev => prev.includes(o) ? prev.filter(x => x !== o) : [...prev, o]);

  return (
    <Page {...temaProps}><Container>
      <TopBar>
        <DocTitle>
          {!claro && <span>✍️</span>}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: streaming ? 480 : 200 }}>{meta?.document_title || 'Evaluación'}</span>
          <IdiomaPill>{(idioma || 'es').toUpperCase()}</IdiomaPill>
        </DocTitle>
        <div style={{ fontSize: '.82rem', opacity: .7 }}>{Math.min(idx + 1, total)} / {total}</div>
      </TopBar>

      <Dots>{Array.from({ length: total || 1 }).map((_, i) => <Dot key={i} $done={i < idx || (i === idx && respondida)} $current={i === idx && !respondida} />)}</Dots>

      <Card>
        {actual?.tipo && <TipoChip>{(streaming ? TIPO_S : TIPO_LABEL)[actual.tipo] || actual.tipo}</TipoChip>}
        <PreguntaNum>Pregunta {Math.min(idx + 1, total)} de {total}</PreguntaNum>
        <Pregunta>{actual?.pregunta_texto}</Pregunta>

        {!respondida && esOpciones && !esMultiple && (
          <div style={{ marginTop: '.5rem' }}>
            {opciones.map((o, i) => (
              <OpcionBtn key={i} $sel={seleccion === o} disabled={thinking}
                onClick={() => { setSeleccion(o); enviarRespuesta(o); }}>
                {actual.tipo === 'seleccion' ? `${String.fromCharCode(65 + i)}. ${o}` : o}
              </OpcionBtn>
            ))}
          </div>
        )}

        {!respondida && esMultiple && (
          <div style={{ marginTop: '.5rem' }}>
            <Hint style={{ textAlign: 'left', margin: '0 0 .2rem' }}>Marca todas las respuestas correctas.</Hint>
            {opciones.map((o, i) => (
              <OpcionBtn key={i} $sel={marcadas.includes(o)} disabled={thinking}
                onClick={() => alternarMarcada(o)}>
                {marcadas.includes(o) ? '☑' : '☐'} {String.fromCharCode(65 + i)}. {o}
              </OpcionBtn>
            ))}
            <Btn $primary onClick={() => enviarRespuesta(marcadas.join(' | '))} disabled={thinking || marcadas.length === 0}>
              {thinking ? 'Evaluando…' : 'Enviar respuesta'}
            </Btn>
          </div>
        )}

        {!respondida && !esOpciones && !esMultiple && (
          <>
            <Textarea
              placeholder="Escribe tu respuesta aquí…"
              value={texto}
              onChange={e => setTexto(e.target.value)}
              disabled={thinking}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviarRespuesta(texto); } }}
            />
            <Btn $primary onClick={() => enviarRespuesta(texto)} disabled={thinking || !texto.trim()}>
              {thinking ? 'Evaluando…' : 'Enviar respuesta'}
            </Btn>
            <Hint>Ctrl/Cmd + Enter para enviar</Hint>
          </>
        )}

        {respondida && (
          <>
            <FeedbackBox $color={streaming ? (NIVEL_S[ultimoFeedback.nivel] || NIVEL_S.incorrecta).color
              : claro ? (ultimoFeedback.nivel === 'completa' ? 'var(--acento)' : ultimoFeedback.nivel === 'incorrecta' ? 'var(--error)' : ultimoFeedback.color)
              : ultimoFeedback.color}>
              {claro
                ? <div style={{ fontWeight: 800, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4,
                    color: ultimoFeedback.nivel === 'completa' ? 'var(--acento)' : ultimoFeedback.nivel === 'incorrecta' ? 'var(--error)' : ultimoFeedback.color }}>
                    {NIVEL_TXT_C[ultimoFeedback.nivel] || ultimoFeedback.nivel}
                  </div>
                : streaming
                ? <div style={{ fontWeight: 700, color: (NIVEL_S[ultimoFeedback.nivel] || NIVEL_S.incorrecta).color, fontSize: '.88rem', marginBottom: 4 }}>{(NIVEL_S[ultimoFeedback.nivel] || NIVEL_S.incorrecta).txt}</div>
                : <div style={{ fontWeight: 800, color: ultimoFeedback.color, textTransform: 'uppercase', fontSize: '.72rem', marginBottom: 4 }}>
                    {NIVEL_EMOJI[ultimoFeedback.nivel]} {ultimoFeedback.nivel}
                  </div>}
              <div>{ultimoFeedback.feedback}</div>
              {ultimoFeedback.explicacion && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: claro ? '1px solid #d3ebe8' : '1px solid rgba(255,255,255,.12)', fontSize: '.88rem', lineHeight: 1.55, opacity: .92 }}>
                  {ultimoFeedback.explicacion}
                </div>
              )}
            </FeedbackBox>
            <Btn $primary onClick={avanzar}>
              {idx + 1 >= preguntas.length ? 'Ver resultado' : 'Siguiente pregunta'}
            </Btn>
          </>
        )}
      </Card>
    </Container></Page>
  );
};

export default EvaluacionEscritaPage;
